import { Tracks } from "@fluidattacks/tracks";
import capitalize from "lodash/capitalize";
import type { RefObject } from "react";
import { useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";

import {
  hasValue,
  resolveOrganization,
  shouldQueryOrganizations,
  useOrgFromUrl,
  useUserOrganizations,
} from "./utils";

import { authContext } from "context/auth";
import type { IEventEvidenceAttr } from "pages/event/evidence/types";
import type { IEvidenceItem } from "pages/finding/evidence/types";
import { getEnvironment } from "utils/environment";

interface IUseEvidenceAudit {
  addEvidenceAudit: (
    evidences: Record<string, IEvidenceItem | IEventEvidenceAttr>,
    fileName: string,
    instance: {
      id: string;
      name: "Event" | "Finding";
    },
    videoViewStatus?: "start" | "half" | "end",
  ) => void;
}

interface IUseAudit {
  addAuditEvent: (object: string, objectId: string, group?: string) => void;
}

interface IUseObserverAudit {
  ref: RefObject<HTMLDivElement>;
}

const OBSERVER_DELAY = 1000;

const useAudit = (): IUseAudit => {
  const { userEmail } = useContext(authContext);
  const { groupName, organizationName } = useParams<{
    groupName: string;
    organizationName: string;
  }>();
  const orgFromUrl = useOrgFromUrl();
  const shouldQuery = shouldQueryOrganizations(organizationName, orgFromUrl);

  const { data: orgData } = useUserOrganizations(undefined, !shouldQuery);

  const resolvedOrgName = useMemo((): string => {
    if (hasValue(organizationName)) return organizationName ?? "";
    if (hasValue(orgFromUrl)) return orgFromUrl ?? "";

    return resolveOrganization(orgFromUrl, orgData?.me.organizations);
  }, [organizationName, orgFromUrl, orgData]);

  const client = useMemo((): Tracks => new Tracks(), []);

  const addAuditEvent = useCallback(
    (object: string, objectId: string, group?: string): void => {
      if (getEnvironment() !== "production") {
        return;
      }

      const eventGroup = group ?? groupName;
      const eventOrganization = organizationName ?? resolvedOrgName;

      client.event.create({
        action: "READ",
        author: userEmail || "unknown",
        date: new Date(),
        mechanism: "WEB",
        metadata: {
          ...(hasValue(eventGroup) ? { group: eventGroup } : {}),
          ...(hasValue(eventOrganization)
            ? { organization: eventOrganization }
            : {}),
          referer: document.referrer,
        },
        object,
        objectId,
      });
    },
    [client, groupName, organizationName, resolvedOrgName, userEmail],
  );

  return { addAuditEvent };
};

const useEvidenceAudit = (): IUseEvidenceAudit => {
  const { addAuditEvent } = useAudit();
  const addEvidenceAudit = useCallback(
    (
      evidences: Record<string, IEvidenceItem | IEventEvidenceAttr>,
      fileName: string,
      instance: {
        id: string;
        name: "Event" | "Finding";
      },
      videoViewStatus?: "start" | "half" | "end",
    ): void => {
      const filteredEvidenceEntry = Object.entries(evidences).find(
        ([_, evidence]: [
          string,
          IEvidenceItem | IEventEvidenceAttr,
        ]): boolean => {
          if ("url" in evidence) {
            return evidence.url === fileName;
          }
          if ("fileName" in evidence) {
            return evidence.fileName === fileName;
          }

          return false;
        },
      );

      if (filteredEvidenceEntry) {
        const [key] = filteredEvidenceEntry;
        const getObjectName = (): string => {
          const baseName = `${instance.name}.Evidence.${capitalize(key)}`;
          if (videoViewStatus) {
            return `${baseName}.${capitalize(videoViewStatus)}`;
          }

          return baseName;
        };
        addAuditEvent(getObjectName(), instance.id);
      }
    },
    [addAuditEvent],
  );

  return { addEvidenceAudit };
};

const useObserverAudit = (
  object: string,
  objectId: string,
): IUseObserverAudit => {
  const ref = useRef<HTMLDivElement>(null);
  const { addAuditEvent } = useAudit();

  useEffect((): (() => void) => {
    if (typeof (window as { Cypress?: unknown }).Cypress !== "undefined")
      return (): void => undefined;

    if (!ref.current) return (): void => undefined;
    const currentRef = ref.current;

    const observer = new IntersectionObserver(
      ([entry]): void => {
        if (entry.isIntersecting) addAuditEvent(object, objectId);
      },
      { threshold: 0.9 },
    );

    const timeoutId = setTimeout((): void => {
      observer.observe(currentRef);
    }, OBSERVER_DELAY);

    return (): void => {
      clearTimeout(timeoutId);
      observer.unobserve(currentRef);
    };
  }, [addAuditEvent, object, objectId]);

  return { ref };
};

export { useAudit, useEvidenceAudit, useObserverAudit };
